import { routes } from "../apiroutes";
import HttpService from "../httpServices";
import useFetchItem from "../useFetchItem";
import useMutateItem from "../useMutateItem";
import { ErrorHandler } from "../errorHandler";
import { showErrorAlert, showSuccessAlert } from "../../utils/alert";

const httpService = new HttpService();

// Productivity Reports Hooks
export const useGetEmployeeProductivity = ({ employeeId, enabled = true, filters = {} } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["GetEmployeeProductivity", employeeId, filters],
    queryFn: () => httpService.getData(routes.getEmployeeProductivity(employeeId, filters)),
    enabled: enabled && !!employeeId,
    retry: 2
  });

  return {
    employeeProductivityIsFetching: isFetching,
    employeeProductivityIsFetched: isFetched,
    employeeProductivityIsLoading: isLoading,
    employeeProductivityError: ErrorHandler(error),
    employeeProductivityData: data?.data || {},
    refetchEmployeeProductivity: refetch
  };
};

export const useGetTeamProductivity = ({ departmentId, enabled = true, filters = {} } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["GetTeamProductivity", departmentId, filters],
    queryFn: () => httpService.getData(routes.getTeamProductivity(departmentId, filters)),
    enabled: enabled && !!departmentId,
    retry: 2
  });

  return {
    teamProductivityIsFetching: isFetching,
    teamProductivityIsFetched: isFetched,
    teamProductivityIsLoading: isLoading,
    teamProductivityError: ErrorHandler(error),
    teamProductivityData: data?.data || {},
    refetchTeamProductivity: refetch
  };
};

export const useGetOrganizationProductivity = ({ enabled = true } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["GetOrganizationProductivity"],
    queryFn: () => httpService.getData(routes.getOrganizationProductivity()),
    enabled,
    retry: 2
  });

  return {
    organizationProductivityIsFetching: isFetching,
    organizationProductivityIsFetched: isFetched,
    organizationProductivityIsLoading: isLoading,
    organizationProductivityError: ErrorHandler(error),
    organizationProductivityData: data?.data || {},
    refetchOrganizationProductivity: refetch
  };
};

export const useGetProductivityRanking = ({ enabled = true, filters = {} } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["GetProductivityRanking", filters],
    queryFn: () => httpService.getData(routes.getProductivityRanking(filters)),
    enabled,
    retry: 2
  });

  return {
    productivityRankingIsFetching: isFetching,
    productivityRankingIsFetched: isFetched,
    productivityRankingIsLoading: isLoading,
    productivityRankingError: ErrorHandler(error),
    productivityRankingData: data?.data || {},
    refetchProductivityRanking: refetch
  };
};

export const useCalculateProductivityMetrics = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (params) =>
      httpService.postData({}, routes.calculateProductivityMetrics(params)),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message || "Metrics calculated successfully");
      handleSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    calculateMetricsData: data,
    calculateMetricsError: ErrorHandler(error),
    calculateMetricsIsLoading: isPending,
    calculateMetricsPayload: (requestPayload) => mutate(requestPayload),
  };
}; 